import TimeOps from '../data/TimeOps';

/**
 * Utility class for session-related operations
 */
class SessionUtils {
    /**
     * Filters and sorts sessions based on criteria
     * @param sessions - Object containing all sessions
     * @param showPastSessions - Whether to show past or upcoming sessions
     * @returns Filtered and sorted sessions object
     */
    static filterAndSortSessions(sessions: any, showPastSessions: boolean, preserveOrder: boolean = false): any {
        const filteredEntries = Object.entries(sessions).filter(([sessionId, sessionData]) => {
            if (!sessionData || (sessionData as any).deleted) return false;
            const status = TimeOps.getSessionStatus(sessionData);
            if (showPastSessions) {
                return status === 'past';
            }
            return status !== 'past';
        });

        if (preserveOrder) {
            // Keep original insertion order (no sorting), just return filtered
            return Object.fromEntries(filteredEntries);
        }

        const sortedEntries = filteredEntries
            .slice()
            .sort(([, sessionA], [, sessionB]) => {
                const rawA = (sessionA as any).officialStartDate || (sessionA as any).startDate;
                const rawB = (sessionB as any).officialStartDate || (sessionB as any).startDate;

                const parseToDate = (val: any): Date => {
                    if (!val) return new Date(8640000000000000); // max date to push missing to end
                    if (val?.toDate) {
                        try { return val.toDate(); } catch { /* noop */ }
                    }
                    if (typeof val === 'string') {
                        const cleaned = val.trim().replace(/['"]/g, '');
                        const d = new Date(cleaned);
                        if (!isNaN(d.getTime())) return d;
                        // Try MM/DD/YYYY fallback
                        const parts = cleaned.split('/');
                        if (parts.length === 3) {
                            const mm = parseInt(parts[0], 10) - 1;
                            const dd = parseInt(parts[1], 10);
                            const yy = parseInt(parts[2], 10);
                            const alt = new Date(yy, mm, dd);
                            if (!isNaN(alt.getTime())) return alt;
                        }
                    }
                    const dflt = new Date(val);
                    return !isNaN(dflt.getTime()) ? dflt : new Date(8640000000000000);
                };

                const dA = parseToDate(rawA);
                const dB = parseToDate(rawB);
                return dA.getTime() - dB.getTime(); // earliest to latest
            });

        return Object.fromEntries(sortedEntries);
    }

    /**
     * Creates a default session object
     * @param camp - Camp object
     * @param sessionId - Session ID
     * @returns Default session object
     */
    static createDefaultSession(camp: any, sessionId: string): any {
        return {
            campAddress: camp.campAddress,
            campId: camp.campId,
            campName: camp.campName,
            title: '',
            capacity: '',
            price: '',
            startDate: '',
            endDate: '',
            createdAt: new Date(),
            startAt: null,
            endAt: null,
            startAge: '',
            endAge: '',
            startTime: '',
            endTime: '',
            sessionType: 'DAY',
            registrationStatus: 'REGISTER',
            sessionId: sessionId,
            coach: {
                userId: null,
                firstName: 'TBD',
                lastName: 'Coach',
                email: null,
                phone: null,
            },
        };
    }

    /** Map day string to bit index: Sun=0 .. Sat=6 */
    private static dayToBitIndex(day: string): number {
        const map: Record<string, number> = {
            'Sun': 0, 'Sunday': 0, 'Su': 0,
            'Mon': 1, 'Monday': 1, 'M': 1,
            'Tue': 2, 'Tues': 2, 'Tuesday': 2, 'Tu': 2,
            'Wed': 3, 'Wednesday': 3, 'W': 3,
            'Thu': 4, 'Thur': 4, 'Thurs': 4, 'Thursday': 4, 'Th': 4,
            'Fri': 5, 'Friday': 5, 'F': 5,
            'Sat': 6, 'Saturday': 6, 'Sa': 6,
        };
        return map[day] ?? -1;
    }

    /** Convert array of days to a 7-bit mask (bit0=Sun .. bit6=Sat) */
    static daysOfWeekToMask(days: string[] | undefined | null): number {
        if (!days || days.length === 0) return 0;
        let mask = 0;
        for (const d of days) {
            const idx = this.dayToBitIndex(d);
            if (idx >= 0) mask |= (1 << idx);
        }
        return mask;
    }

    /** Convert 7-bit mask (bit0=Sun .. bit6=Sat) to canonical day strings ['Sun','Mon',...] */
    static maskToDaysOfWeek(mask: number | undefined | null): string[] {
        if (!mask || mask <= 0) return [];
        const canonical = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
        const out: string[] = [];
        for (let i = 0; i < 7; i++) {
            if ((mask & (1 << i)) !== 0) out.push(canonical[i]);
        }
        return out;
    }

    /** True if all 7 days are selected */
    static isEveryday(days?: string[] | null, mask?: number | null): boolean {
        if (typeof mask === 'number') return mask === 0b1111111; // 127
        if (Array.isArray(days)) return days.length === 7;
        return false;
    }

    /** Format days like "(MWF)" using abbreviations: Su M Tu W Th F Sa */
    static formatDaysAbbrev(days?: string[] | null, mask?: number | null): string {
        const canonical = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
        const abbrMap: Record<string, string> = {
            'Sun': 'Su', 'Mon': 'M', 'Tue': 'Tu', 'Wed': 'W', 'Thu': 'Th', 'Fri': 'F', 'Sat': 'Sa'
        };
        let list: string[] = [];
        if (Array.isArray(days) && days.length > 0) {
            list = days.slice();
        } else if (typeof mask === 'number' && mask > 0) {
            list = this.maskToDaysOfWeek(mask);
        }
        if (list.length === 0) return '';
        // Sort by canonical week order
        list.sort((a, b) => canonical.indexOf(a) - canonical.indexOf(b));
        const abbr = list.map(d => abbrMap[d] || d).join('');
        return `(${abbr})`;
    }
}

export default SessionUtils;

